import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Navigate, Routes, Route } from "react-router-dom";
import { LazyMotion, MotionConfig, domAnimation } from "framer-motion";
import LandingPage from "./Pages/LandingPage";
import useMotionProfile from "./hooks/useMotionProfile";
import { AppMotionProvider } from "./context/AppMotionContext";
import { SiteConfigProvider } from "./context/SiteConfigContext";
import { prefetchPublicPlans } from "./utils/plansApi";
import { prefetchPublicFaqs } from "./utils/faqsApi";
import { prefetchPublicTestimonials } from "./utils/testimonialsApi";
import { prefetchPublicTerms } from "./utils/termsApi";
import { prefetchPublicPrivacy } from "./utils/privacyApi";
import { prefetchPublicRefund } from "./utils/refundApi";
import { prefetchSiteConfig } from "./utils/siteConfigApi";
import ToastHost from "./components/common/ToastHost";
import ScrollToTop from "./components/layout/ScrollToTop";

const About = lazy(() => import("./Pages/About"));
const Contact = lazy(() => import("./Pages/Contact"));
const Franchise = lazy(() => import("./Pages/Franchise"));
const Careers = lazy(() => import("./Pages/Careers"));
const Blog = lazy(() => import("./Pages/Blog"));
const BlogDetail = lazy(() => import("./Pages/BlogDetail"));
const Pricing = lazy(() => import("./Pages/Pricing"));
const Features = lazy(() => import("./Pages/Features"));
const FeatureDetail = lazy(() => import("./Pages/FeatureDetail"));
const HowItWorks = lazy(() => import("./Pages/HowItWorks"));
const Integrations = lazy(() => import("./Pages/Integrations"));
const Api = lazy(() => import("./Pages/Api"));
const Documentation = lazy(() => import("./Pages/Documentation"));
// const Tutorials = lazy(() => import("./Pages/Tutorials"));
// const TutorialDetail = lazy(() => import("./Pages/TutorialDetail"));
const Faq = lazy(() => import("./Pages/Faq"));
const Support = lazy(() => import("./Pages/Support"));
const Terms = lazy(() => import("./Pages/Terms"));
const Privacy = lazy(() => import("./Pages/Privacy"));
const Refund = lazy(() => import("./Pages/Refund"));
const CookiePolicy = lazy(() => import("./Pages/CookiePolicy"));
const Login = lazy(() => import("./Pages/Login"));
const Register = lazy(() => import("./Pages/Register"));
const ForgotPassword = lazy(() => import("./Pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./Pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./Pages/VerifyEmail"));

function usePrefetchAppRoutes() {
  useEffect(() => {
    prefetchPublicPlans();
    prefetchPublicFaqs();
    prefetchPublicTestimonials();
    prefetchPublicTerms();
    prefetchPublicPrivacy();
    prefetchPublicRefund();
    prefetchSiteConfig();

    const routes = [
      () => import("./Pages/Features"),
      () => import("./Pages/Pricing"),
      () => import("./Pages/Faq"),
      () => import("./Pages/About"),
      () => import("./Pages/Contact"),
      () => import("./Pages/HowItWorks"),
      () => import("./Pages/Integrations"),
      () => import("./Pages/Login"),
    ];
    const prefetch = () => routes.forEach((load) => void load());
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(prefetch, { timeout: 5000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = window.setTimeout(prefetch, 3000);
    return () => window.clearTimeout(t);
  }, []);
}

function App() {
  const { reduceMotion } = useMotionProfile();
  usePrefetchAppRoutes();

  return (
    <LazyMotion features={domAnimation} strict>
      <MotionConfig reducedMotion={reduceMotion ? "always" : "user"}>
        <AppMotionProvider>
          <SiteConfigProvider>
          <Router>
            <ScrollToTop />
            <ToastHost />
            <Suspense fallback={null}>
              <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/franchise" element={<Franchise />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/features" element={<Features />} />
              <Route path="/features/:slug" element={<FeatureDetail />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/api" element={<Api />} />
              <Route path="/documentation" element={<Documentation />} />
              {/* <Route path="/tutorials" element={<Tutorials />} /> */}
              {/* <Route path="/tutorials/:slug" element={<TutorialDetail />} /> */}
              <Route path="/faq" element={<Faq />} />
              <Route path="/support" element={<Support />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Router>
          </SiteConfigProvider>
        </AppMotionProvider>
      </MotionConfig>
    </LazyMotion>
  );
}

export default App;
